import React, { useState, useEffect } from 'react';
import { RevoltModal } from '../../../../../components/RevoltModal';
import { RevoltInput } from '../../../../../components/RevoltInput';
import { RevoltButton } from '../../../../../components/RevoltButton';
import { ProductEmbedOrImage } from '../../../components/ProductEmbedOrImage';
import './ProductImageModal.css';

export function ProductImageModal({
  isOpen,
  onClose,
  product,
  currentThumbnail = '',
  currentImages = [],
  onUpdateImages
}) {
  const [thumbnail, setThumbnail] = useState(currentThumbnail);
  const [images, setImages] = useState([...currentImages]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      setThumbnail(currentThumbnail);
      setImages([...currentImages]);
      setCurrentImageIndex(0);
      setNewImageUrl('');
      setIsSubmitting(false);
    }
  }, [isOpen, currentThumbnail, currentImages]);

  const handleAddImage = () => {
    const url = newImageUrl.trim();
    if (url && !images.includes(url)) {
      const newImages = [...images, url];
      setImages(newImages);
      setCurrentImageIndex(newImages.length - 1); // Switch to the newly added image
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (imageToRemove) => {
    const newImages = images.filter(img => img !== imageToRemove);
    setImages(newImages);

    // Adjust current index if necessary
    if (newImages.length === 0) {
      setCurrentImageIndex(0);
    } else if (currentImageIndex >= newImages.length) {
      setCurrentImageIndex(newImages.length - 1);
    }
  };

  const handleImageKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddImage();
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      await onUpdateImages(thumbnail, images);
      onClose();
    } catch (error) {
      console.error('Failed to update images:', error);
      alert('Failed to update images. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Product Images"
    >
      <div className="product-image-modal">
        <div className="image-modal-columns">
          {/* Left Column - Thumbnail */}
          <div className="image-modal-column">
            <div className="modal-section">
              <label className="modal-label">Thumbnail Image</label>
              <div className="thumbnail-preview">
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt="Product thumbnail"
                    className="thumbnail-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200x150/333/666?text=Invalid+URL';
                    }}
                  />
                ) : (
                  <div className="thumbnail-placeholder">
                    <i className="fa-solid fa-image"></i>
                    <span>No thumbnail</span>
                  </div>
                )}
              </div>
              <RevoltInput
                type="text"
                value={thumbnail}
                onChange={(e) => setThumbnail(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="thumbnail-input"
              />
              <div className="form-help">
                Main product image for listings and cards (image URL only).
              </div>
            </div>
          </div>

          {/* Right Column - Gallery Images */}
          <div className="image-modal-column">
            <div className="modal-section">
              <label className="modal-label">Gallery (images or YouTube)</label>
              {images.length > 0 ? (
                <div className="gallery-carousel">
                  {/* Main Gallery Image Display */}
                  <div className="gallery-image-container">
                    <ProductEmbedOrImage
                      url={images[currentImageIndex]}
                      title={`${product?.name || 'Product'} gallery ${currentImageIndex + 1}`}
                      imageClassName="gallery-main-image"
                      onImageError={(e) => {
                        e.target.src = 'https://via.placeholder.com/200x150/333/666?text=Invalid';
                      }}
                    />
                    <button
                      type="button"
                      className="gallery-image-remove"
                      onClick={() => handleRemoveImage(images[currentImageIndex])}
                      aria-label={`Remove current image`}
                    >
                      <i className="fa-solid fa-times"></i>
                    </button>

                    {/* Navigation Arrows */}
                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="gallery-nav gallery-nav--prev"
                          onClick={handlePrevImage}
                          aria-label="Previous gallery image"
                        >
                          <i className="fa-solid fa-chevron-left"></i>
                        </button>
                        <button
                          type="button"
                          className="gallery-nav gallery-nav--next"
                          onClick={handleNextImage}
                          aria-label="Next gallery image"
                        >
                          <i className="fa-solid fa-chevron-right"></i>
                        </button>
                      </>
                    )}
                  </div>

                  {/* Image Counter */}
                  {images.length > 1 && (
                    <div className="gallery-counter">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-images-container">
                  <p className="no-images">No gallery images yet</p>
                </div>
              )}
            </div>

            {/* Add New Image Section */}
            <div className="modal-section">
              <label className="modal-label">Add Gallery Image</label>
              <div className="add-image-group">
                <RevoltInput
                  type="text"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  onKeyPress={handleImageKeyPress}
                  placeholder="Image URL or YouTube link"
                />
                <RevoltButton
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={handleAddImage}
                  disabled={!newImageUrl.trim() || images.includes(newImageUrl.trim())}
                >
                  <i className="fa-solid fa-plus"></i>
                  Add
                </RevoltButton>
              </div>
              <div className="form-help">
                Additional slides in the product gallery — image URLs or YouTube links.
              </div>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <RevoltButton
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>
          <RevoltButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Updating...
              </>
            ) : (
              'Update Images'
            )}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}